import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import i18n, { type Resource } from 'i18next';
import { Component, StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { initReactI18next } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import { QueryClientProvider } from '~/providers/QueryClientProvider';
import { initAnalytics } from '~/services/analytics';

import App from './App';
import { company } from './company/Company';
import { CompanyName } from './company/interface';
import Translations from './translations.json';
import TranslationsSaaS from './translations-saas.json';
import { getCookieValue } from './utils/cookies';

if (process.env.NODE_ENV === 'development') {
  import('eruda').then(({ default: eruda }) => {
    eruda.init();
  });
}

Sentry.init({
  dsn: 'https://0d0291e4f0fd4f45a654bb68b5a1f336@sentry.jiffy-team.com/25',
  integrations: [browserTracingIntegration()],
  environment: import.meta.env.DEV
    ? 'development'
    : import.meta.env.REACT_APP_ENV || 'development',
  tracesSampleRate: 1.0,
  maxValueLength: 5000,
  normalizeDepth: 0,
  enabled: import.meta.env.PROD,
  release: import.meta.env.REACT_APP_RELEASE
    ? `frontend-cd@${import.meta.env.REACT_APP_RELEASE}`
    : undefined,
  beforeBreadcrumb(breadcrumb) {
    if (
      breadcrumb.category === 'xhr' &&
      breadcrumb.data &&
      breadcrumb.data.url.includes('yandex')
    ) {
      return null;
    }
    if (breadcrumb.category === 'sentry.transaction') {
      return null;
    }
    return breadcrumb;
  },
});

class HydrationErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  private errorLoggerRestored = false;
  private errorsList = [
    'Text content does not match server-rendered HTML.',
    'Text content did not match.',
    'Hydration failed because the initial UI does not match what was rendered on the server.',
    'There was an error while hydrating.',
  ];

  listHasErrors = (errorText: string) => {
    return this.errorsList.some((i) => errorText.includes(i));
  };

  suppressHydrationWarnings() {
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      if (!args.length) {
        return;
      }
      const msg = typeof args[0] === 'string' ? args[0] : '';
      if (this.listHasErrors(msg)) {
        return;
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }

  static getDerivedStateFromError(error: Error) {
    if (
      error?.message?.includes('Text content does not match') ||
      error?.message?.includes('Warning: Text content did not match') ||
      error?.message?.includes('Hydration failed') ||
      error?.message?.includes('There was an error while hydrating') ||
      error?.message?.includes('An error occurred during hydration') ||
      error?.message?.includes('Prop `placeholder` did not match')
    ) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  private restoreLogger: (() => void) | null = null;

  componentDidMount() {
    this.restoreLogger = this.suppressHydrationWarnings();
    this.errorLoggerRestored = true;
    window.addEventListener('error', this.handleGlobalError);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
    if (this.restoreLogger) {
      this.restoreLogger();
      this.restoreLogger = null;
    }
  }

  handleGlobalError = (event: ErrorEvent) => {
    const isHydrationError =
      event.error?.message && this.listHasErrors(event.error?.message);

    if (isHydrationError) {
      event.preventDefault();
      return true;
    }
  };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (!error?.message || !this.listHasErrors(error?.message)) {
      Sentry.withScope((scope) => {
        scope.setExtra('errorInfo', errorInfo);
        Sentry.captureException(error);
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

const getCurrentTranslation = () => {
  const companyName = company.name;
  switch (companyName) {
    case CompanyName.CityDrinks:
      return Translations;
    default:
      return TranslationsSaaS;
  }
};

i18n.use(initReactI18next).init({
  resources: getCurrentTranslation() as Resource,
  lng: getCookieValue('lang') ?? company.config.lang,
  fallbackLng: company.config.lang,
  interpolation: {
    escapeValue: false,
    defaultVariables: {
      companyName: company.config.name,
      companyNameSecondary: company.config.nameSecondary,
      companyEmail: company.config.email,
      ageRestricted: company.config.ageRestricted,
    },
  },
});

const origin = window.location.origin;

const manifests = {
  [CompanyName.CityDrinks]: {
    name: 'CityDrinks',
    short_name: 'CityDrinks',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-citydrinks/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-citydrinks/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-citydrinks/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-citydrinks/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.DubaiExpress]: {
    name: 'DubaiExpress',
    short_name: 'DubaiExpress',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-dubaiexpress/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-dubaiexpress/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-dubaiexpress/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-dubaiexpress/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Localee]: {
    name: 'Localee',
    short_name: 'Localee',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-localee/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-localee/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-localee/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-localee/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.CircleK]: {
    name: 'CircleK',
    short_name: 'CircleK',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-vilo/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-vilo/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-vilo/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-vilo/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Vilo]: {
    name: 'Vilo Gelato',
    short_name: 'Vilo Gelato',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-vilo/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-vilo/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-vilo/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-vilo/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Jiffy]: {
    name: 'Jiffy',
    short_name: 'Jiffy',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-jiffy/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-jiffy/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-jiffy/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-jiffy/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Kids]: {
    name: 'Our Kids',
    short_name: 'Our Kids',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-ourkids/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-ourkids/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-ourkids/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-ourkids/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Okie]: {
    name: 'Okie Electronics UAE',
    short_name: 'OKIEU',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-ourkids/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-ourkids/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-ourkids/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-ourkids/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Oskuhus]: {
    name: 'Öskuhús',
    short_name: 'Öskuhús',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-oskuhus/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-oskuhus/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-oskuhus/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-oskuhus/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Heimkaup]: {
    name: 'Heimkaup',
    short_name: 'Heimkaup',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-heimkaup/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-heimkaup/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-heimkaup/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-heimkaup/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Cerise]: {
    name: 'Cerise',
    short_name: 'Cerise',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-cerise/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-cerise/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-cerise/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-cerise/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Storrd]: {
    name: 'Storrd Ltd',
    short_name: 'Storrd Ltd',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-storrd/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-storrd/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-storrd/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-storrd/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.MCosmetic]: {
    name: 'MCosmetic',
    short_name: 'MCosmetic',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-mcosmetic/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-mcosmetic/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-mcosmetic/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-mcosmetic/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Ocado]: {
    name: 'Ocado',
    short_name: 'Ocado',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-ocado/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '152x152',
      },
      {
        src: `${origin}/favicon-ocado/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-ocado/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-ocado/safari-pinned-tab.svg`,
        rel: 'mask-icon',
        sizes: '16x16',
      },
    ],
  },
};

if (manifests[company.name as keyof typeof manifests]) {
  const stringManifest = JSON.stringify(
    manifests[company.name as keyof typeof manifests],
  );
  const blob = new Blob([stringManifest], { type: 'application/json' });
  const manifestURL = URL.createObjectURL(blob);
  document
    .querySelector('#manifest-placeholder')
    ?.setAttribute('href', manifestURL);

  let link = document.querySelector(
    "link[rel='shortcut icon']",
  ) as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    if (document?.getElementsByTagName('head')?.[0]) {
      document.getElementsByTagName('head')[0]!.appendChild(link);
    }
  } else if (manifests[company.name as keyof typeof manifests]) {
    link.href = `/favicon-${company.name}/favicon.ico`;
  }
}

initAnalytics();

// Apply design system version and company attributes to body
document.body.setAttribute(
  'data-design',
  company.newDesignSystemEnabled ? 'v3' : 'v2',
);
document.body.setAttribute('data-company', company.name);

/**
 The following code is replacing favicons for the dev env
 with the same icons but different colors.
 It's not used in production.
 */
// if (import.meta.env.DEV) {
//   const favicon = document.querySelectorAll<HTMLLinkElement>('link[rel=icon]');
//   favicon.forEach((link) => {
//     const newHref = link.href.replace(/(\/public\/)(.+)/, '$1development/$2');
//     return (link.href = newHref);
//   });
// }

ReactDOM.hydrateRoot(
  document.getElementById('app')!,
  <HydrationErrorBoundary>
    <StrictMode>
      <QueryClientProvider>
        <BrowserRouter>
          <App globalContext={window.__INITIAL_STATE__?.context} />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>
  </HydrationErrorBoundary>,
);
